import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { normalizeQrDestination, QR_STYLES } from '@/lib/qr';
import { qrSvg, type QrColor, type QrShape } from '@/lib/qrSvg';
import { qrUrl } from '@/lib/hosts';
import { isCell } from '@/lib/cell/config';
import { cellGet, cellPost } from '@/lib/cell/api';

/** The downloaded file carries the pro's own name (Peter, 2026-08-20: "can the download
 *  be in the name of whoever's logged in?"). Lowercased, non-letters stripped, honest
 *  fallback when the profile has no first name yet. */
export function qrFileName(firstName: string | null | undefined): string {
  const clean = (firstName ?? '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  return clean ? `${clean}-qr.png` : 'my-image-qr.png';
}

/**
 * YOUR QR CODE — the pro's window into their dynamic code (Peter, 2026-08-20: the capability
 * existed but was "very hidden" — changing an Instagram handle silently re-pointed the door
 * sign, and nobody was told).
 *
 * The rules, exactly as Peter set them: by default the code follows the pro's own profile
 * (website, then Instagram — the same order the /q/ resolver reads); set an explicit link here
 * and THAT wins; clear it and the code goes back to following the profile. No approval flow.
 * The write is the pro's own row, RLS-fenced (qr_code_pro_update_own).
 *
 * The scan address is imageos.co/q/<slug> — the printed half that never changes. The SVG here
 * is drawn by the SAME generator that prints the door signs (see lib/qrSvg.ts), in the code's
 * saved style, so the PNG a pro downloads matches the ink on their door.
 */

type QrRow = {
  slug: string;
  destination: string | null;
  style_color: string | null;
  style_shape: string | null;
  scan_count: number;
};

const SCAN_BASE = qrUrl('');

export function QrCodeCard({ proId, firstName, websiteUrl, instagram, businessInstagram }: {
  proId: string;
  firstName: string | null;
  websiteUrl: string | null;
  instagram: string | null;
  businessInstagram: string | null;
}) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [styling, setStyling] = useState(false);
  const [input, setInput] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const { data: row, isLoading } = useQuery<QrRow | null>({
    queryKey: ['my-qr', proId],
    queryFn: async () => {
      if (isCell()) {
        // CELL (M28): GET /v1/pro-hub/pros/{proId}/qr — the same select, filters, order and limit.
        const { data, error } = await cellGet<{ row: QrRow | null }>(`/v1/pro-hub/pros/${proId}/qr`);
        if (error) throw error;
        return data?.row ?? null;
      }
      const { data, error } = await supabase
        .from('qr_code')
        .select('slug, destination, style_color, style_shape, scan_count')
        .eq('pro_id', proId)
        .eq('subject_kind', 'pro')
        .is('archived_at', null)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle<QrRow>();
      if (error) throw error;
      return data ?? null;
    },
  });

  const save = useMutation({
    mutationFn: async (raw: string) => {
      const trimmed = raw.trim();
      const dest = trimmed ? normalizeQrDestination(trimmed) : '';
      if (trimmed && !dest) {
        throw new Error("That doesn't look like a link. Try a full address (yoursite.com) or an Instagram handle (@you).");
      }
      if (isCell()) {
        // CELL (M28): POST /v1/pro-hub/pros/{proId}/qr/destination — the same update; rows back = the codes updated.
        const { data, error } = await cellPost<{ rows: Array<{ slug: string }> }>(`/v1/pro-hub/pros/${proId}/qr/destination`, { destination: dest || null });
        if (error) throw new Error(`That did not save — ${error.message}`);
        if (!data?.rows?.length) throw new Error('That did not save — your code could not be updated. Try again, or ask the studio.');
        return;
      }
      const { data, error } = await supabase
        .from('qr_code')
        .update({ destination: dest || null, updated_at: new Date().toISOString() })
        .eq('pro_id', proId)
        .eq('subject_kind', 'pro')
        .select('slug'); // qr_code has no id column — slug is its key
      if (error) throw new Error(`That did not save — ${error.message}`);
      // HUB-063: a write the database quietly matched to no row is a failure, not a save.
      if (!data?.length) throw new Error('That did not save — your code could not be updated. Try again, or ask the studio.');
    },
    onSuccess: () => {
      setEditing(false);
      setInput('');
      setErr(null);
      void queryClient.invalidateQueries({ queryKey: ['my-qr', proId] });
    },
    onError: (e) => setErr((e as Error).message),
  });

  // THE STYLE PICKER, REUSED (Peter, 2026-08-20: "we already have the code — why not just
  // reuse"). The 12 tiles are the Owner Hub's own QR_STYLES — every one rendered by the same
  // generator that prints and decoder-proven before it was allowed in — so a pro picks from
  // exactly the palette the owner's QR Studio offers. Picking saves immediately, Canva-style.
  const saveStyle = useMutation({
    mutationFn: async (style: { color: string; shape: string }) => {
      if (isCell()) {
        // CELL (M28): POST /v1/pro-hub/pros/{proId}/qr/style — the same update; rows back = the codes updated.
        const { data, error } = await cellPost<{ rows: Array<{ slug: string }> }>(`/v1/pro-hub/pros/${proId}/qr/style`, { color: style.color, shape: style.shape });
        if (error) throw new Error(`That style did not save — ${error.message}`);
        if (!data?.rows?.length) throw new Error('That style did not save — your code could not be updated. Try again.');
        return;
      }
      const { data, error } = await supabase
        .from('qr_code')
        .update({ style_color: style.color, style_shape: style.shape, updated_at: new Date().toISOString() })
        .eq('pro_id', proId)
        .eq('subject_kind', 'pro')
        .select('slug'); // qr_code has no id column — slug is its key
      if (error) throw new Error(`That style did not save — ${error.message}`);
      if (!data?.length) throw new Error('That style did not save — your code could not be updated. Try again.');
    },
    onSuccess: () => {
      setStyling(false);
      void queryClient.invalidateQueries({ queryKey: ['my-qr', proId] });
    },
  });

  if (isLoading) return null;
  if (!row) {
    // No code minted yet — the owner mints from the Owner Hub's QR Studio. Say so honestly
    // rather than rendering a dead control.
    return (
      <div className="card !p-4">
        <div className="text-xs font-bold tracking-[0.12em] uppercase text-image-pink">Your QR Code</div>
        <p className="text-xs text-image-charcoal mt-1.5">
          Your dynamic QR code is being set up by the studio — it will appear here once it's ready.
        </p>
      </div>
    );
  }

  // Mirror the resolver's exact order so this card never lies about where a scan lands:
  // explicit choice → website → Instagram → business Instagram → the studio's own landing.
  const explicit = normalizeQrDestination(row.destination ?? '');
  const profileSource = websiteUrl?.trim() ? 'website'
    : instagram?.trim() ? 'Instagram'
    : businessInstagram?.trim() ? 'Instagram' : null;
  const profileUrl = normalizeQrDestination(websiteUrl?.trim() || instagram?.trim() || businessInstagram?.trim() || '');
  const target = explicit || profileUrl;
  const mode: 'choice' | 'profile' | 'studio' = explicit ? 'choice' : profileUrl ? 'profile' : 'studio';

  const color = (row.style_color === 'pink' ? 'pink' : 'ink') as QrColor;
  const shape = (row.style_shape ?? 'square') as QrShape;
  const svg = qrSvg(SCAN_BASE + row.slug, 220, { color, shape });

  const downloadPng = async () => {
    // Print-resolution PNG of the same code, same style. Rasterized client-side — the SVG is
    // ours, so there is nothing to fetch and nothing to pay for.
    const big = qrSvg(SCAN_BASE + row.slug, 1024, { color, shape });
    const url = URL.createObjectURL(new Blob([big], { type: 'image/svg+xml' }));
    try {
      const img = new Image();
      await new Promise<void>((res, rej) => { img.onload = () => res(); img.onerror = () => rej(new Error('render failed')); img.src = url; });
      const canvas = document.createElement('canvas');
      canvas.width = canvas.height = 1024;
      canvas.getContext('2d')!.drawImage(img, 0, 0);
      canvas.toBlob((blob) => {
        if (!blob) return;
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = qrFileName(firstName);
        a.click();
        URL.revokeObjectURL(a.href);
      }, 'image/png');
    } finally {
      URL.revokeObjectURL(url);
    }
  };

  return (
    <div className="card !p-4">
      <div className="text-xs font-bold tracking-[0.12em] uppercase text-image-pink">Your QR Code</div>
      <div className="mt-3 flex flex-col sm:flex-row gap-4 sm:items-start">
        <div className="shrink-0 mx-auto sm:mx-0 rounded-xl ring-1 ring-image-border overflow-hidden"
          style={{ width: 220, height: 220 }}
          aria-label="Your QR code"
          dangerouslySetInnerHTML={{ __html: svg }}
        />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold text-image-black">On your door sign — and yours to use anywhere.</p>
          <p className="text-xs text-image-charcoal mt-1">
            It's dynamic: the printed code never changes, but where it goes is up to you.
            Change it here and every copy updates instantly — no reprints, ever.
          </p>

          <div className="mt-3 text-xs">
            <span className="text-image-charcoal">Right now it goes to </span>
            {target
              ? <a href={target} target="_blank" rel="noreferrer" className="font-semibold text-image-black underline decoration-image-pink/50 break-all">{target.replace(/^https:\/\//, '')}</a>
              : <span className="font-semibold">your studio's page</span>}
            <span className="ml-1.5 inline-block align-middle text-[10px] font-bold tracking-wide uppercase rounded-full px-2 py-0.5 bg-image-fog text-image-charcoal">
              {mode === 'choice' ? 'Your choice' : mode === 'profile' ? `Following your ${profileSource}` : 'Studio default'}
            </span>
          </div>

          {editing ? (
            <div className="mt-3">
              <input
                className="w-full rounded-lg border border-image-border px-3 py-2 focus:outline-none focus:border-image-pink"
                placeholder="yoursite.com, a booking link, or @yourhandle"
                value={input}
                onChange={(e) => { setInput(e.target.value); setErr(null); }}
                autoFocus
              />
              {err && <p className="text-xs text-red-600 mt-1">{err}</p>}
              <div className="flex gap-2 mt-2">
                <button
                  onClick={() => save.mutate(input)}
                  disabled={save.isPending}
                  className="bg-image-pink text-white text-xs font-extrabold rounded-full px-4 py-1.5 disabled:opacity-50">
                  {save.isPending ? 'Saving…' : 'Save'}
                </button>
                <button
                  onClick={() => { setEditing(false); setErr(null); }}
                  className="text-xs font-semibold text-image-charcoal px-2">
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2 mt-3">
              <button
                onClick={() => { setEditing(true); setInput(explicit ? explicit.replace(/^https:\/\//, '') : ''); }}
                className="bg-image-pink text-white text-xs font-extrabold rounded-full px-4 py-1.5">
                Change where it points
              </button>
              <button
                onClick={() => { void downloadPng(); }}
                className="text-xs font-extrabold rounded-full px-4 py-1.5 ring-1 ring-image-border text-image-black hover:ring-image-pink">
                Download PNG
              </button>
              <button
                onClick={() => setStyling(v => !v)}
                className="text-xs font-extrabold rounded-full px-4 py-1.5 ring-1 ring-image-border text-image-black hover:ring-image-pink"
                aria-expanded={styling}>
                Change the style
              </button>
              {mode === 'choice' && (
                <button
                  onClick={() => save.mutate('')}
                  disabled={save.isPending}
                  className="text-xs font-semibold text-image-charcoal px-2 underline decoration-image-border hover:text-image-black">
                  {save.isPending ? 'Saving…' : 'Follow my profile again'}
                </button>
              )}
            </div>
          )}
          {/* HUB-063: "Follow my profile again" saves from the non-editing branch — its failure is shown here. */}
          {!editing && err && <p role="alert" className="text-xs text-red-600 mt-2">{err}</p>}

          {styling && (
            <div className="mt-3 grid grid-cols-4 sm:grid-cols-6 gap-2" role="listbox" aria-label="QR style">
              {QR_STYLES.map(s => {
                const on = s.color === color && s.shape === shape;
                return (
                  <button key={s.id} title={s.label} aria-label={s.label} aria-selected={on} role="option"
                    onClick={() => saveStyle.mutate({ color: s.color, shape: s.shape })}
                    disabled={saveStyle.isPending}
                    className={`rounded-lg overflow-hidden ring-2 transition-all ${on ? 'ring-image-pink' : 'ring-image-border hover:ring-image-pink/60'}`}
                    dangerouslySetInnerHTML={{ __html: qrSvg(SCAN_BASE + row.slug, 64, { color: s.color as QrColor, shape: s.shape as QrShape }) }}
                  />
                );
              })}
            </div>
          )}
          {saveStyle.isError && (
            <p role="alert" className="text-xs text-red-600 mt-2">{(saveStyle.error as Error).message}</p>
          )}

          <p className="text-[11px] text-image-charcoal mt-3">
            {row.scan_count > 0 && <span className="font-semibold">Scanned {row.scan_count.toLocaleString()} {row.scan_count === 1 ? 'time' : 'times'} · </span>}
            Your code stays live for as long as you're a pro at the studio.
          </p>
        </div>
      </div>
    </div>
  );
}
